import { db, storage } from './firebase';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// 직원 집주소 좌표 업데이트 (지도에서 찍은 위치)
export async function updateMemberLocation(mcode, lat, lng, addr) {
  await updateDoc(doc(db, 'members', String(mcode)), {
    addr1: addr || '',
    addr2: '',
    home_lat: String(lat),
    home_lng: String(lng),
  });
}

// ── 간단한 메모리 캐시 (members / logins / activeMembers) — 5분 TTL ──
const CACHE_TTL_MS = 5 * 60 * 1000;
const _cache = { members: null, logins: null, active: null };

export function clearMemberCache() {
  _cache.members = null;
  _cache.logins = null;
  _cache.active = null;
}

// 직원 전체 조회
export async function fetchMembers() {
  const now = Date.now();
  if (_cache.members && now - _cache.members.ts < CACHE_TTL_MS) {
    return _cache.members.data;
  }
  const snap = await getDocs(collection(db, 'members'));
  const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  _cache.members = { data, ts: now };
  return data;
}

// 직원 1명 조회
export async function fetchMemberByCode(mcode) {
  const docRef = doc(db, 'members', String(mcode));
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// 로그인 정보 전체 조회
export async function fetchLogins() {
  const now = Date.now();
  if (_cache.logins && now - _cache.logins.ts < CACHE_TTL_MS) {
    return _cache.logins.data;
  }
  const snap = await getDocs(collection(db, 'logins'));
  const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  _cache.logins = { data, ts: now };
  return data;
}

// 재직자 조회 (l_delete !== 'Y' && l_enable !== 'N') — mcode, displayName 포함
export async function fetchActiveMembers() {
  const now = Date.now();
  if (_cache.active && now - _cache.active.ts < CACHE_TTL_MS) {
    return _cache.active.data;
  }
  const [members, logins] = await Promise.all([fetchMembers(), fetchLogins()]);
  const loginNameMap = {};
  const activeSet = new Set();
  // 로그인중지(l_enable='W')는 재직자이므로 목록에는 남기되 플래그를 실어준다.
  // 배정·담당자 선택처럼 "앞으로 일을 맡길 사람"을 고르는 곳에서만 걸러 쓰고,
  // 과거 메시지의 이름/색 매핑 같은 표시용 조회에서는 빼면 안 된다. (2026-09-06)
  const loginStopSet = new Set();
  logins.forEach(l => {
    if (l.l_code && l.l_name) loginNameMap[l.l_code] = l.l_name;
    if (l.id && l.l_name) loginNameMap[l.id] = l.l_name;
    if (l.l_delete !== 'Y' && l.l_enable !== 'N') {
      activeSet.add(l.l_code || l.id);
      if (l.l_enable === 'W') loginStopSet.add(l.l_code || l.id);
    }
  });
  const result = members
    .filter(m => activeSet.has(m.id))
    .map(m => ({
      ...m,
      mcode: m.id,
      displayName: loginNameMap[m.id] || m.bang_name || m.id,
      loginStopped: loginStopSet.has(m.id),
    }));
  _cache.active = { data: result, ts: now };
  return result;
}

// 지점 전체 조회
export async function fetchStores() {
  const snap = await getDocs(collection(db, 'stores'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// SHA-512 해시 (Web Crypto API)
async function sha512(text) {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-512', buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// 로그인 검증 — l_userid + l_pwd(SHA-512) 매칭, 성공 시 사원 정보 반환.
// 동일 l_userid로 여러 row가 존재할 수 있음(재입사로 같은 사번을 다시 받는 케이스 등).
// 이 경우 활성(l_delete!='Y' && l_enable!='N') row를 우선 선택 — 옛 삭제 row가
// 먼저 잡혀 "삭제된 계정"으로 잘못 거절되던 이슈(0377 김보라 등) 해결.
export async function verifyLogin(loginId, password) {
  const snap = await getDocs(collection(db, 'logins'));
  const allMatches = snap.docs.filter(d => {
    const data = d.data();
    return data.l_userid === loginId || d.id === loginId;
  });
  if (allMatches.length === 0) return { success: false, error: 'ID가 존재하지 않습니다.' };

  // 활성 row 우선 — 비활성/삭제된 row는 매칭 후보에서 제외
  const activeMatches = allMatches.filter(d => {
    const x = d.data();
    return x.l_delete !== 'Y' && x.l_enable !== 'N';
  });
  if (activeMatches.length === 0) {
    // 모두 비활성. 가장 흔한 사유 한 가지를 메시지로 반환
    const first = allMatches[0].data();
    if (first.l_delete === 'Y') return { success: false, error: '삭제된 계정입니다.' };
    if (first.l_enable === 'N') return { success: false, error: '비활성화된 계정입니다. 관리자에게 문의하세요.' };
    return { success: false, error: '로그인할 수 없는 계정입니다.' };
  }

  const hashed = await sha512(password);
  const match = activeMatches.find(d => d.data().l_pwd === hashed);
  if (!match) return { success: false, error: '비밀번호가 일치하지 않습니다.' };

  const login = { id: match.id, ...match.data() };

  // members에서 상세 정보 가져오기
  const mcode = login.l_code || login.id;
  const memberSnap = await getDoc(doc(db, 'members', String(mcode)));
  const member = memberSnap.exists() ? { id: memberSnap.id, ...memberSnap.data() } : {};

  // 지점명 조회 (선택적)
  let storeName = '';
  if (member.store_idx) {
    try {
      const storesSnap = await getDocs(collection(db, 'stores'));
      const st = storesSnap.docs.find(sd => {
        const s = sd.data();
        return String(s.idx || s.no || sd.id) === String(member.store_idx);
      });
      if (st) storeName = st.data().name || '';
    } catch {}
  }

  return {
    success: true,
    user: {
      mcode,
      name: login.l_name || member.bang_name || loginId,
      loginId: login.l_userid,
      dept: member.dept || '',
      store_idx: member.store_idx || '',
      store_name: storeName,
      team_no: member.team_no || '',
      hp: [member.hp1, member.hp2, member.hp3].filter(Boolean).join('-'),
      email: member.email || '',
      photo_save: member.photo_save || '사용중지',
      ip_check: member.ip_check || '', // 직원별 IP 체크 적용여부('사용'/'사용중지'). 로그인 IP 게이트에서 참조
      isAdmin: (member.dept === '대표' || login.l_userid === 'admin' || member.isAdmin === true),
      // 초기비번(0000)으로 로그인했거나 변경필요 플래그면 새 비번 재설정 강제
      needPasswordChange: login.l_change_pwd === 'Y' || password === '0000',
    }
  };
}

// 비밀번호 변경
export async function changePassword(mcode, newPassword) {
  const hashed = await sha512(newPassword);
  const ref = doc(db, 'logins', String(mcode));
  await setDoc(ref, { l_pwd: hashed, l_change_pwd: 'N' }, { merge: true });
}

// 내 프로필 원본 조회 (member + login 합본)
export async function fetchMyProfile(mcode) {
  const mSnap = await getDoc(doc(db, 'members', String(mcode)));
  const lSnap = await getDoc(doc(db, 'logins', String(mcode)));
  const member = mSnap.exists() ? mSnap.data() : {};
  const login = lSnap.exists() ? lSnap.data() : {};
  return { member, login };
}

// 내 프로필 수정. mcode/dept/store/team/직책/입사일 등은 변경 불가.
export async function updateMyProfile(mcode, patch) {
  const memberRef = doc(db, 'members', String(mcode));
  // 전화번호(hp1/hp2/hp3)는 의도적으로 제외 — 퇴사 직전 임의 변경 방지
  const stringFields = [
    'email', 'birthday', 'hobby', 'kakaoid',
    'zip', 'addr1', 'addr2',
    'bank_name', 'bank_account', 'account_holder',
    'image', 'nametag_image',
  ];
  const mPatch = {};
  stringFields.forEach((k) => {
    if (typeof patch[k] === 'string') mPatch[k] = patch[k];
  });
  if (typeof patch.bang_name === 'string') mPatch.bang_name = patch.bang_name;
  if (Array.isArray(patch.group_images)) {
    mPatch.group_images = patch.group_images.filter(Boolean);
  }
  await setDoc(memberRef, mPatch, { merge: true });

  // 이름 변경 시 logins.l_name 동기화
  if (typeof patch.bang_name === 'string' && patch.bang_name.trim()) {
    const loginRef = doc(db, 'logins', String(mcode));
    await setDoc(loginRef, { l_name: patch.bang_name.trim() }, { merge: true });
  }
}

// 단일 사진 업로드 (프로필/명함) → members.<field> 저장
// kind: 'image' | 'nametag_image'
export async function uploadMyImage(mcode, kind, file) {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `members/${mcode}/${kind}_${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const url = await getDownloadURL(r);
  await setDoc(doc(db, 'members', String(mcode)), { [kind]: url }, { merge: true });
  return url;
}

// 단체사진 다수 업로드 — group_images 배열에 append
export async function uploadMyGroupImages(mcode, files) {
  const urls = [];
  for (const file of Array.from(files || [])) {
    if (!file?.type?.startsWith('image/')) continue;
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `members/${mcode}/group_${Date.now()}_${Math.random().toString(36).slice(2, 6)}.${ext}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);
    urls.push(url);
  }
  if (urls.length === 0) return [];

  // 기존 배열 + 신규 배열 병합
  const memberRef = doc(db, 'members', String(mcode));
  const snap = await getDoc(memberRef);
  const existing = snap.exists() ? snap.data().group_images : null;
  const prev = Array.isArray(existing) ? existing.filter(Boolean) : [];
  // 레거시 단일 group_image 필드도 포함
  if (snap.exists()) {
    const legacy = snap.data().group_image;
    if (legacy && !prev.includes(legacy)) prev.push(legacy);
  }
  const next = [...prev, ...urls];
  await setDoc(memberRef, { group_images: next, group_image: '' }, { merge: true });
  return urls;
}

// 기존 호환용 alias
export async function uploadMyProfileImage(mcode, file) {
  return uploadMyImage(mcode, 'image', file);
}

// 현재 비밀번호 일치 확인
export async function verifyCurrentPassword(mcode, password) {
  const loginSnap = await getDoc(doc(db, 'logins', String(mcode)));
  if (!loginSnap.exists()) return false;
  const hashed = await sha512(password);
  return loginSnap.data().l_pwd === hashed;
}

// 새 mcode 생성 (members 최대값 + 1, 없으면 타임스탬프)
async function generateMcode() {
  const snap = await getDocs(collection(db, 'members'));
  let max = 0;
  snap.docs.forEach(d => {
    const n = Number(d.id);
    if (!isNaN(n) && n > max) max = n;
  });
  if (max > 0) return String(max + 1);
  return `M${Date.now()}`;
}

// 구성원 추가 (members + logins 동시 생성) — 확장 필드 지원
export async function addMember(data) {
  const mcode = data.mcode || await generateMcode();
  const { l_id, l_pwd, l_name, l_enable, l_delete, ...memberData } = data;
  await setDoc(doc(db, 'members', String(mcode)), {
    ...memberData,
    mcode,
    bang_name: memberData.bang_name || l_name || '',
  }, { merge: true });

  const loginDoc = {
    l_code: mcode,
    l_name: l_name || memberData.bang_name || '',
    l_id: l_id || mcode,
    l_userid: l_id || mcode,
    l_pwd: l_pwd ? await sha512(l_pwd) : await sha512('0000'),
    l_change_pwd: l_pwd ? 'N' : 'Y', // 비번 미지정(기본 0000)이면 최초 로그인 시 재설정 강제
    l_delete: l_delete || 'N',
    l_enable: l_enable || 'Y',
    l_regdate: new Date().toISOString().slice(0, 10),
  };
  await setDoc(doc(db, 'logins', String(mcode)), loginDoc, { merge: true });
  clearMemberCache();
  return mcode;
}

// 구성원 수정 (members + logins 동시 업데이트)
export async function updateMember(mcode, data) {
  const { l_id, l_pwd, l_name, l_enable, l_delete, ...memberData } = data;
  await setDoc(doc(db, 'members', String(mcode)), memberData, { merge: true });
  const loginUpdate = {};
  // 아이디는 값이 있을 때만 갱신 — 빈 문자열로 덮어써 로그인 아이디가 지워지는 버그 방지
  // (레거시/임포트 계정은 logins에 l_userid가 없어 폼에서 ''로 들어올 수 있음)
  if (l_id !== undefined && String(l_id).trim() !== '') {
    loginUpdate.l_id = l_id;
    loginUpdate.l_userid = l_id;
  }
  if (l_name !== undefined) loginUpdate.l_name = l_name;
  if (l_enable !== undefined) loginUpdate.l_enable = l_enable;
  if (l_delete !== undefined) loginUpdate.l_delete = l_delete;
  if (l_pwd) loginUpdate.l_pwd = await sha512(l_pwd);
  if (Object.keys(loginUpdate).length > 0) {
    await setDoc(doc(db, 'logins', String(mcode)), loginUpdate, { merge: true });
  }
  clearMemberCache();
}

// 구성원 삭제 (퇴사 처리 — soft delete)
// 기존 사이트 기준: l_enable='N' = 퇴사 (+ l_delete='Y' 하위호환)
export async function deleteMember(mcode) {
  await setDoc(doc(db, 'logins', String(mcode)), { l_enable: 'N', l_delete: 'Y' }, { merge: true });
  clearMemberCache();
}
